import { useNavigate } from "react-router";

import { t } from "@lingui/core/macro";
import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@readai/common/hooks/useToasts";
import { withQueryParams } from "@readai/common/utils";

import {
  createRssLinkCalendarEvent,
  patchRssLinkCalendarEvent,
} from "@app/booking/api/rssLinkCalendarEvents";
import ROUTES from "@app/booking/routes";
import type {
  ApiRssLinkCalendarEvent,
  RssLinkCalendarEvent,
} from "@app/booking/types/rssLinkTypes";

import { BASE_QUERY_KEY as BASE_AVAILABILITY_QUERY_KEY } from "./useAvailability";
import { getQueryKey as getEventQueryKey } from "./useRssLinkCalendarEvent";

const useScheduleEvent = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const clearPreviousAvailability = () => {
    queryClient.removeQueries({
      queryKey: BASE_AVAILABILITY_QUERY_KEY,
    });
  };

  const scheduleEvent = useMutation({
    mutationFn: createRssLinkCalendarEvent,
    onSettled: clearPreviousAvailability,
    onSuccess: (newEvent: ApiRssLinkCalendarEvent) => {
      navigate(
        withQueryParams(ROUTES.eventById(newEvent.id), {
          success: true,
          action: "create",
        }),
      );
    },
    onError: (error) => {
      Sentry.captureException(error);
      toast.error(
        t`Sorry, we were unable to schedule this event. Please try again or pick a different time.`,
      );
    },
  });

  const rescheduleEvent = useMutation({
    mutationFn: patchRssLinkCalendarEvent,
    onMutate: async ({ eventId, patchBody }) => {
      const queryKey = getEventQueryKey(eventId);
      await queryClient.cancelQueries({ queryKey });
      const prevData = queryClient.getQueryData(
        queryKey,
      ) as RssLinkCalendarEvent;
      const newData = { ...prevData, ...patchBody };
      queryClient.setQueryData(queryKey, newData);
      return prevData;
    },
    onError: (error, { eventId }, ctx) => {
      queryClient.setQueryData(getEventQueryKey(eventId), ctx);
      Sentry.captureException(error);
      toast.error(
        t`Sorry, we were unable to reschedule this event. Please try again or pick a different time.`,
      );
    },
    onSettled: clearPreviousAvailability,
    onSuccess: (_data, { eventId }) => {
      navigate(
        withQueryParams(ROUTES.eventById(eventId), {
          success: true,
          action: "reschedule",
        }),
      );
    },
  });

  return { scheduleEvent, rescheduleEvent };
};

export default useScheduleEvent;
